/**
 * FAQ List — sidebar scroll-spy + smooth-scroll-on-click.
 *
 * Behavior:
 *   - Click a sidebar link → smooth-scroll the matching category just below
 *     the sticky header. URL hash is updated without jumping.
 *   - As the user scrolls, the link whose category is in the upper third of
 *     the viewport gets the .is-active class (underlined).
 *   - Re-initialises on shopify:section:load (Theme Editor).
 *   - JS-free fallback: anchor links still jump natively, <details> still
 *     toggle, no broken state.
 */

(function () {
  const SECTION_SELECTOR = '[data-faq-section]';
  const NAV_LINK_SELECTOR = '[data-faq-nav-link]';
  const CATEGORY_SELECTOR = '[data-faq-category]';
  const DETAILS_SELECTOR = '.faq__details';
  const DESKTOP_QUERY = '(min-width: 1440px)';

  function getHeaderOffset() {
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue('--header-height').trim();
    if (!value) return 80;
    if (value.endsWith('rem')) {
      const rem = parseFloat(value);
      const base = parseFloat(getComputedStyle(root).fontSize) || 16;
      return rem * base;
    }
    return parseFloat(value) || 80;
  }

  function setActive(links, anchor) {
    links.forEach((link) => {
      const isActive = link.dataset.target === anchor;
      link.classList.toggle('is-active', isActive);
    });
  }

  function initSection(section) {
    if (!section || section._faqInitialized) return;
    section._faqInitialized = true;

    /* ── Exclusive accordion ─────────────────────────────────────────────
       Opening any question must close every other open question across
       all categories in the section. Wired before the nav early-return
       so a section without a sidebar still gets exclusive behaviour. */
    const allDetails = Array.from(section.querySelectorAll(DETAILS_SELECTOR));
    const onDetailsToggle = (event) => {
      const target = event.currentTarget;
      if (!target.open) return;
      allDetails.forEach((d) => {
        if (d !== target && d.open) d.open = false;
      });
    };
    allDetails.forEach((d) => d.addEventListener('toggle', onDetailsToggle));

    const links = Array.from(section.querySelectorAll(NAV_LINK_SELECTOR));
    const categories = Array.from(section.querySelectorAll(CATEGORY_SELECTOR));
    if (links.length === 0 || categories.length === 0) {
      section._faqCleanup = () => {
        allDetails.forEach((d) => d.removeEventListener('toggle', onDetailsToggle));
        section._faqInitialized = false;
      };
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── Click → smooth scroll ───────────────────────────────────────── */
    const onLinkClick = (event) => {
      const link = event.currentTarget;
      const targetId = link.dataset.target;
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      event.preventDefault();
      const offset = getHeaderOffset() + 24;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });

      setActive(links, targetId);

      if (history.replaceState) {
        history.replaceState(null, '', `#${targetId}`);
      }
    };

    links.forEach((link) => link.addEventListener('click', onLinkClick));

    /* ── Scroll-spy via IntersectionObserver ─────────────────────────── */
    let observer = null;
    const visibility = new Map();

    const updateActive = () => {
      let bestId = null;
      let bestRatio = -1;
      visibility.forEach((ratio, id) => {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = id;
        }
      });
      if (bestId) setActive(links, bestId);
    };

    const buildObserver = () => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.id;
            if (!id) return;
            visibility.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
          });
          updateActive();
        },
        {
          rootMargin: '-20% 0px -65% 0px',
          threshold: [0, 0.25, 0.5, 0.75, 1],
        }
      );
      categories.forEach((category) => observer.observe(category));
    };

    /* Only run scroll-spy where the sidebar is visible. */
    const desktopMQ = window.matchMedia(DESKTOP_QUERY);

    const enableSpy = () => {
      if (observer) return;
      buildObserver();
    };

    const disableSpy = () => {
      if (!observer) return;
      observer.disconnect();
      observer = null;
      visibility.clear();
    };

    const onMQChange = (event) => {
      if (event.matches) enableSpy();
      else disableSpy();
    };

    if (desktopMQ.matches) enableSpy();

    if (typeof desktopMQ.addEventListener === 'function') {
      desktopMQ.addEventListener('change', onMQChange);
    } else if (typeof desktopMQ.addListener === 'function') {
      // Safari < 14 fallback
      desktopMQ.addListener(onMQChange);
    }

    /* ── Cleanup helper for section unload ───────────────────────────── */
    section._faqCleanup = () => {
      allDetails.forEach((d) => d.removeEventListener('toggle', onDetailsToggle));
      links.forEach((link) => link.removeEventListener('click', onLinkClick));
      disableSpy();
      if (typeof desktopMQ.removeEventListener === 'function') {
        desktopMQ.removeEventListener('change', onMQChange);
      } else if (typeof desktopMQ.removeListener === 'function') {
        desktopMQ.removeListener(onMQChange);
      }
      section._faqInitialized = false;
    };
  }

  function destroySection(section) {
    if (section && section._faqCleanup) {
      section._faqCleanup();
      delete section._faqCleanup;
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
